import React from "react";
import {
  VoteCandidate,
  Experience,
  Project,
  Contest,
  Misc,
  Score,
} from "../types";
import { generateLogoLinkUrl } from "../services/brandfetch";

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import WorkIcon from "@mui/icons-material/WorkOutline";
import CodeIcon from "@mui/icons-material/Code";
import EmojiEventsIcon from "@mui/icons-material/EmojiEventsOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import ScoreboardIcon from "@mui/icons-material/ScoreboardOutlined";
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined";

interface StudentCardProps {
  student: VoteCandidate;
  onVote: () => void;
  isPreview?: boolean;
}

const StudentCard: React.FC<StudentCardProps> = ({
  student,
  onVote,
  isPreview,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const renderSection = (
    title: string,
    IconComponent: React.ElementType,
    items: Experience[] | Project[] | Contest[] | Misc[] | Score[] | undefined,
    primaryField: string,
    secondaryField?: string
  ) => {
    const hasItems = items && items.length > 0;

    if (!hasItems) {
      return null;
    }

    return (
      <Box
        sx={{
          mb: isMobile ? 1.2 : 2.5,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1.5,
            mb: isMobile ? 0.5 : 1.5,
            pb: isMobile ? 0.25 : 0.75,
          }}
        >
          <IconComponent
            sx={{
              fontSize: isMobile ? "0.9rem" : "1.25rem",
              color: "rgba(51, 51, 51, 0.7)",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontSize: isMobile ? "0.7rem" : "0.95rem",
              lineHeight: 1.5,
              fontWeight: 400,
              color: "#333333",
              letterSpacing: "0.025em",
            }}
          >
            {title}
          </Typography>
        </Box>
        <List
          dense
          disablePadding
          sx={{
            pl: isMobile ? 0.5 : 1.5,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {items.map((item, index) => {
            let logoUrl: string | null = null;
            if (title === "Experience" && (item as Experience).company_url) {
              try {
                const fullUrl = (item as Experience).company_url as string;
                const urlWithProto =
                  fullUrl.startsWith("http://") ||
                  fullUrl.startsWith("https://")
                    ? fullUrl
                    : `https://${fullUrl}`;
                const urlObject = new URL(urlWithProto);
                const domain = urlObject.hostname.replace(/^www\./, "");
                if (domain && domain.trim() !== "") {
                  logoUrl = generateLogoLinkUrl(domain, {
                    type: "icon",
                    height: isMobile ? 14 : 28,
                  });
                }
              } catch (e) {
                console.warn(
                  `[StudentCard] Invalid company URL for item ${
                    item.id || index
                  }:`,
                  (item as Experience).company_url,
                  e
                );
              }
            }

            const titleValue = item[primaryField as keyof typeof item];
            const companyValue = secondaryField
              ? item[secondaryField as keyof typeof item]
              : null;
            let primaryText;

            if (title === "Experience") {
              primaryText =
                companyValue && String(companyValue).trim() !== ""
                  ? `${titleValue} @ ${companyValue}`
                  : titleValue;
            } else {
              primaryText = titleValue;
            }

            return (
              <ListItem
                key={item.id || index}
                disableGutters
                sx={{
                  py: isMobile ? 0 : 0.1,
                  display: "flex",
                  alignItems: "center",
                  maxWidth: "100%",
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                {title === "Experience" ? (
                  <>
                    {typeof (item as Experience).company_url === "string" &&
                      (item as Experience).company_url != null &&
                      (item as Experience).company_url !== "" &&
                      ((item as Experience).company_url as string).trim() !==
                        "" && (
                        <Box
                          component="img"
                          src={
                            generateLogoLinkUrl(
                              ((item as Experience).company_url as string)
                                .replace(/^https?:\/\//, "")
                                .replace(/\/$/, ""),
                              {
                                type: "icon",
                                fallback: "lettermark",
                                height: isMobile ? 14 : 20,
                              }
                            ) || ""
                          }
                          alt={`${companyValue || "Company"} logo`}
                          sx={{
                            height: isMobile ? 14 : 20,
                            width: "auto",
                            mr: isMobile ? 0.5 : 1,
                            verticalAlign: "middle",
                            flexShrink: 0,
                            objectFit: "contain",
                          }}
                          onError={(e) =>
                            ((
                              e.currentTarget as HTMLImageElement
                            ).style.display = "none")
                          }
                        />
                      )}
                    <ListItemText
                      primary={primaryText}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontSize: isMobile ? "0.65rem" : "0.875rem",
                        fontWeight: 300,
                        color: "#333333",
                        sx: {
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          width: "100%",
                          display: "block",
                        },
                      }}
                      sx={{
                        m: 0,
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                      }}
                    />
                  </>
                ) : title === "Projects" ? (
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "hidden",
                          wordBreak: isMobile ? "break-word" : "normal",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 300,
                            color: "#333333",
                            display: isMobile ? "inline" : "inline-block",
                            wordBreak: isMobile ? "break-word" : "normal",
                          }}
                        >
                          {item[primaryField as keyof typeof item]}
                        </Box>
                        {secondaryField &&
                          item[secondaryField as keyof typeof item] &&
                          String(
                            item[secondaryField as keyof typeof item]
                          ).trim() !== "" && (
                            <Box
                              component="span"
                              sx={{
                                color: "rgba(51, 51, 51, 0.7)",
                                fontWeight: 300,
                                ml: 0.5,
                                display: isMobile ? "inline" : "inline-block",
                                wordBreak: isMobile ? "break-word" : "normal",
                              }}
                            >{`- ${
                              item[secondaryField as keyof typeof item]
                            }`}</Box>
                          )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: isMobile ? "0.65rem" : "0.875rem",
                      fontWeight: 300,
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    sx={{
                      m: 0,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  />
                ) : title === "Contests" ? (
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "hidden",
                          wordBreak: isMobile ? "break-word" : "normal",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 300,
                            color: "#333333",
                            display: isMobile ? "inline" : "inline-block",
                            wordBreak: isMobile ? "break-word" : "normal",
                          }}
                        >
                          {item[primaryField as keyof typeof item]}
                        </Box>
                        {secondaryField &&
                        item[secondaryField as keyof typeof item] ? (
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 300,
                              color: "rgba(51, 51, 51, 0.7)",
                              display: isMobile ? "inline" : "inline-block",
                              wordBreak: isMobile ? "break-word" : "normal",
                            }}
                          >{` (${
                            item[secondaryField as keyof typeof item]
                          })`}</Box>
                        ) : (
                          ""
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: isMobile ? "0.65rem" : "0.875rem",
                      fontWeight: 300,
                      sx: {
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    sx={{
                      m: 0,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  />
                ) : title === "Misc" ? (
                  <ListItemText
                    primary={item[primaryField as keyof typeof item]}
                    secondary={
                      secondaryField
                        ? item[secondaryField as keyof typeof item]
                        : null
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: isMobile ? "0.65rem" : "0.875rem",
                      fontWeight: 300,
                      color: "#333333",
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: "rgba(51, 51, 51, 0.7)",
                      fontSize: isMobile ? "0.6rem" : "0.875rem",
                      fontWeight: 300,
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    sx={{
                      m: 0,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  />
                ) : title === "Scores" ? (
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          maxWidth: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 300,
                            color: "#333333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item[primaryField as keyof typeof item]}
                        </Box>
                        {secondaryField &&
                        item[secondaryField as keyof typeof item] ? (
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 300,
                              color: "rgba(51, 51, 51, 0.7)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              wordBreak: "break-word",
                            }}
                          >{`: ${
                            item[secondaryField as keyof typeof item]
                          }`}</Box>
                        ) : (
                          ""
                        )}
                        {(item as Score).verified && (
                          <VerifiedIcon
                            sx={{
                              ml: 0.5,
                              fontSize: isMobile ? "0.6rem" : "0.8rem",
                              color: "rgba(16, 185, 129, 0.8)",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: isMobile ? "0.65rem" : "0.875rem",
                      fontWeight: 300,
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    sx={{
                      m: 0,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  />
                ) : (
                  <ListItemText
                    primary={item[primaryField as keyof typeof item]}
                    secondary={
                      secondaryField
                        ? item[secondaryField as keyof typeof item]
                        : null
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: isMobile ? "0.65rem" : "0.875rem",
                      fontWeight: 300,
                      color: "#333333",
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "rgba(51, 51, 51, 0.7)",
                      fontSize: isMobile ? "0.6rem" : "0.75rem",
                      fontWeight: 300,
                      sx: {
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        width: "100%",
                        display: "block",
                      },
                    }}
                    sx={{
                      m: 0,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Paper
      onClick={!isPreview ? onVote : undefined}
      elevation={0}
      sx={{
        p: isMobile ? 1.5 : 3,
        borderRadius: "4px",
        cursor: !isPreview ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8f8f8",
        height: "100%",
        width: "100%",
        overflowWrap: "break-word",
        wordWrap: "break-word",
        hyphens: "auto",
        overflow: "auto",
        border: "1px solid rgba(51, 51, 51, 0.2)",
        minHeight: "100%",
        overflowY: "auto",
      }}
    >
      {renderSection(
        "Experience",
        WorkIcon,
        student.experiences,
        "title",
        "company"
      )}
      {renderSection(
        "Contests",
        EmojiEventsIcon,
        student.contests,
        "name",
        "description"
      )}
      {renderSection("Scores", ScoreboardIcon, student.scores, "name", "value")}
      {renderSection(
        "Projects",
        CodeIcon,
        student.projects,
        "name",
        "description"
      )}
      {renderSection("Misc", InfoIcon, student.misc, "value")}

      {(!student.experiences || student.experiences.length === 0) &&
        (!student.projects || student.projects.length === 0) &&
        (!student.contests || student.contests.length === 0) &&
        (!student.misc || student.misc.length === 0) &&
        (!student.scores || student.scores.length === 0) && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              color="rgba(51, 51, 51, 0.5)"
              sx={{
                mt: isMobile ? 1 : 2,
                textAlign: "center",
                fontWeight: 300,
                fontSize: isMobile ? "0.65rem" : "0.875rem",
              }}
            >
              No profile details available.
            </Typography>
          </Box>
        )}
    </Paper>
  );
};

export default StudentCard;
